'use client';

import { Input, InputOtp } from '@heroui/react';
import Image from 'next/image';
import { useRouter, useSearchParams } from 'next/navigation';
import { signIn } from 'next-auth/react';
import { useTranslations } from 'next-intl';
import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import toast, { Toaster } from 'react-hot-toast';
import Button from '@/components/common/button';
import LanguagePopover from '@/components/common/language-popover';
import Modal from '@/components/common/modal';
import IconActivity from '@/components/icons/activity';
import IconAnalytic from '@/components/icons/analytic';
import IconDashboard from '@/components/icons/dashboard';
import IconEye from '@/components/icons/eye';
import IconEyeSlash from '@/components/icons/eye-slash';
import IconGithub from '@/components/icons/github';
import IconGoogle from '@/components/icons/google';
import IconHeart from '@/components/icons/heart';
import IconLinkedin from '@/components/icons/linkedin';
import IconLogo from '@/components/icons/logo';
import IconMenuTodo from '@/components/icons/menu-todo';
import IconX from '@/components/icons/x';
import fetchApi from '@/services/fetch';
import appConfig from '@/utils/config/app';
const SignInPage = () => {
  const router = useRouter();
  const t = useTranslations();
  const searchParams = useSearchParams();
  const error = searchParams.get('error');
  const [errState, setErrState] = useState('');
  const [loading, setLoading] = useState(false);
  const [otpError, setOtpError] = useState('');
  const {
    handleSubmit,
    control
  } = useForm({
    defaultValues: {
      email: '',
      password: '',
      name: ''
    }
  });
  const [isVisible, setIsVisible] = useState(false);
  const [isSignUp, setIsSignUp] = useState(false);
  const [verification, setVerification] = useState({
    email: '',
    otp: ''
  });
  const getErrorMessage = errCode => {
    switch (errCode) {
      case 'OAuthAccountNotLinked':
        return t('messages.OAuthAccountNotLinked');
      default:
        return t('messages.error');
    }
  };
  const toggleVisibility = () => setIsVisible(!isVisible);
  const handleLogin = async values => {
    setLoading(true);
    if (isSignUp) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!values.email || !emailRegex.test(values.email)) {
        toast.error(t('validate.validEmail'));
        setLoading(false);
        return;
      }
      if (!values.password) {
        toast.error(t('validate.password'));
        setLoading(false);
        return;
      }
      if (!values.name) {
        toast.error(t('validate.name'));
        setLoading(false);
        return;
      }
      try {
        await fetchApi('/v1/auth/sign-up', {
          body: JSON.stringify(values),
          method: 'POST'
        });
        setVerification({
          email: values.email,
          otp: ''
        });
      } catch (error) {
        toast.error(t('validate.emailExist'));
        //
      }
    } else {
      const res: any = await signIn('credentials', {
        ...values,
        redirect: false
      });
      if (res.error) toast.error(t('messages.loginFailed'));else {
        toast.success(t('messages.loginSucc'));
        const callbackUrl = new URL(res.url).searchParams.get('callbackUrl') || '/app';
        router.push(callbackUrl);
      }
    }
    setLoading(false);
  };
  const handleVerifyOTP = async () => {
    setLoading(true);
    if (isSignUp) {
      try {
        const payload = {
          email: verification.email,
          otp: verification.otp
        };
        await fetchApi('/v1/auth/sign-up/verify-otp', {
          body: JSON.stringify(payload),
          method: 'POST'
        });
        setOtpError('');
        setVerification({
          ...verification,
          email: ''
        });
        toast.success(t('messages.signupSucc'));
        setIsSignUp(false);
      } catch (error) {
        setOtpError(t('messages.invalidOTP'));
        //
      }
      setLoading(false);
    }
  };
  useEffect(() => {
    if (error) setErrState(error);
  }, [error]);
  return <div className='grid min-h-screen grid-cols-2 overflow-x-hidden bg-white text-sm' data-sentry-component="SignInPage" data-sentry-source-file="page.tsx">
      <Toaster data-sentry-element="Toaster" data-sentry-source-file="page.tsx" />
      <div className='col-span-2 lg:col-span-1'>
        <div className='flex h-[80px] items-center justify-between px-10 shadow'>
          <div onClick={() => router.push('/')} className='flex cursor-pointer items-center gap-3'>
            <IconLogo data-sentry-element="IconLogo" data-sentry-source-file="page.tsx" />
            <p className='bg-gradient-to-r from-cl-1 to-cl-5 bg-clip-text pt-1 text-xl font-bold text-transparent'>
              Corodomo
            </p>
          </div>
          <LanguagePopover data-sentry-element="LanguagePopover" data-sentry-source-file="page.tsx" />
        </div>
        <div className='m-auto flex h-[calc(100%-80px)] max-w-lg flex-col justify-center p-6'>
          <form onSubmit={handleSubmit(handleLogin)}>
            <div>
              <p className='text-3xl font-bold'>
                {isSignUp ? t('app.welcomeSignup') : t('app.welcome')}
              </p>
              <p className='mt-2 text-sm text-default-400'>
                {t(isSignUp ? 'app.signupDesc' : 'app.welcomeDesc')}
              </p>
            </div>
            {isSignUp ? null : <Button className='mt-6' fullWidth onPress={() => signIn('google')}>
                <IconGoogle width={20} /> {t('app.loginWith')} Google
              </Button>}
            {isSignUp ? null : <div className='mt-4 flex items-center gap-3'>
                <Button variant='bordered' onPress={() => signIn('linkedin')} fullWidth>
                  <IconLinkedin className='w-5' />
                  <div className='hidden md:block'>Linkedin</div>
                </Button>
                <Button variant='bordered' onPress={() => signIn('twitter')} fullWidth>
                  <IconX width={20} /> <div className='hidden md:block'>X</div>
                </Button>
                <Button variant='bordered' onPress={() => signIn('github')} fullWidth>
                  <IconGithub width={20} />
                  <div className='hidden md:block'>Github</div>
                </Button>
              </div>}
            <div className={`mt-8 border-gray-200 ${isSignUp ? '' : 'border-t pt-2'}`}>
              {isSignUp ? null : <p className='my-4 text-sm text-default-400'>
                  {t('app.loginWithAccount')}
                </p>}
              <p className='mb-2 font-medium'>Email</p>
              <Controller name='email' control={control} render={({
              field
            }) => <Input fullWidth placeholder='Email' {...field} ref={null} />} data-sentry-element="Controller" data-sentry-source-file="page.tsx" />
            </div>
            <div className='mt-6'>
              <p className='mb-2 font-medium'>{t('app.password')}</p>
              <Controller name='password' control={control} render={({
              field
            }) => <Input placeholder={t('app.password')} endContent={<button className='focus:outline-none' type='button' onClick={toggleVisibility}>
                        {isVisible ? <IconEye width={20} className='pointer-events-none text-2xl text-default-400' /> : <IconEyeSlash width={20} className='pointer-events-none text-2xl text-default-400' />}
                      </button>} type={isVisible ? 'text' : 'password'} {...field} ref={null} />} data-sentry-element="Controller" data-sentry-source-file="page.tsx" />
            </div>
            {errState ? <p className='mt-2 text-red-400'>{getErrorMessage(errState)}</p> : null}
            {isSignUp ? <div className='mt-6'>
                <p className='mb-2 font-medium'>{t('app.name')}</p>
                <Controller name='name' control={control} render={({
              field
            }) => <Input placeholder={t('app.name')} type={'text'} {...field} ref={null} />} />
              </div> : null}
            <div className='flex flex-col'>
              <div className='mt-6 flex items-center justify-between'>
                <Button
              // variant='solid'
              // color='primary'
              type='submit' isLoading={loading} data-sentry-element="Button" data-sentry-source-file="page.tsx">
                  {isSignUp ? t('app.register') : t('app.login')}
                </Button>
                <p onClick={() => setIsSignUp(!isSignUp)} className='cursor-pointer text-primary'>
                  {isSignUp ? t('app.signin') : t('app.signup')}
                </p>
              </div>
              {/* <div className='mt-2 flex justify-between'>
                <p>
                  {isSignUp ? t('app.alreadyAccount') : t('app.needAccount')}
                </p>
                <p
                  onClick={() => setIsSignUp(!isSignUp)}
                  className='cursor-pointer text-primary'
                >
                  {isSignUp ? t('app.signin') : t('app.signup')}
                </p>
               </div> */}
            </div>
          </form>
        </div>
      </div>
      <div className='relative col-span-2 bg-cl-bg lg:col-span-1'>
        <div className='size-0 border-r-[150px] border-t-[150px] border-r-transparent border-t-cl-bg-icon lg:border-r-[250px] lg:border-t-[250px]' />
        <div className='absolute left-1/2 top-4 flex max-w-lg -translate-x-1/2 flex-col lg:left-20 lg:top-1/2 lg:mr-8 lg:-translate-y-1/2 lg:translate-x-0 lg:py-4 '>
          <div className='hidden rounded-lg bg-white p-6 shadow lg:block'>
            <div className=''>
              <Image src='/assets/images/landing_1.webp' alt='corodomo' width={400} height={200} className='h-auto w-full rounded-lg object-cover' data-sentry-element="Image" data-sentry-source-file="page.tsx" />
            </div>
            <div className='mt-6 flex items-center justify-between'>
              <div className=''>
                <p className='bg-gradient-to-r from-cl-1 to-cl-5 bg-clip-text text-lg font-bold text-transparent'>
                  {appConfig.title}{' '}
                </p>
                <p>{appConfig.description}</p>
              </div>
              <div className='flex items-center justify-center rounded-full border p-2'>
                <IconHeart width={16} className='text-cl-1-hv' data-sentry-element="IconHeart" data-sentry-source-file="page.tsx" />
              </div>
            </div>
            <div className='my-4 flex items-center gap-4 whitespace-nowrap border-t pt-4'>
              <div className='flex items-center gap-1'>
                <span className='flex-1 text-gray-500'>
                  <IconDashboard width={18} data-sentry-element="IconDashboard" data-sentry-source-file="page.tsx" />
                </span>
                Themes
              </div>
              <div className='flex items-center gap-1'>
                <span className='flex-1 text-gray-500'>
                  <IconActivity width={16} data-sentry-element="IconActivity" data-sentry-source-file="page.tsx" />
                </span>
                <span className='w-full'></span>Ambient sounds
              </div>
              <div className='flex items-center gap-1'>
                <span className='flex-1 text-gray-500'>
                  <IconMenuTodo width={16} data-sentry-element="IconMenuTodo" data-sentry-source-file="page.tsx" />
                </span>
                Todo List
              </div>
              <div className='flex items-center gap-1'>
                <span className='flex-1 text-gray-500'>
                  <IconAnalytic width={16} data-sentry-element="IconAnalytic" data-sentry-source-file="page.tsx" />
                </span>
                Stats
              </div>
            </div>
            <div className='flex items-center justify-between rounded-lg bg-cl-bg p-4 shadow-sm'>
              <div>
                <p className='font-medium'>Let&apos;s start</p>
                <div className='mt-2 flex items-center gap-1'>
                  <p className='text-2xl font-bold text-cl-1'>+400 </p>
                  <p> themes</p>
                </div>
              </div>
              <div>
                <Button size='sm' data-sentry-element="Button" data-sentry-source-file="page.tsx">Start</Button>
              </div>
            </div>
          </div>
          <div className='mt-10'>
            <p className='font-medium'>Powered by Corodomo</p>
            <div className='mt-2 text-xs text-gray-400'>
              You agree to Corodomo&apos;s{' '}
              <a href='/terms-and-privacy' target='_blank' className='text-xs text-cl-1'>
                Terms of Use & Privacy Policy
              </a>
              . You don&apos;t need to consent as a condition of. Message/data
              may apply.
            </div>
          </div>
        </div>
      </div>
      <Modal onAction={handleVerifyOTP} isDismissable={false} header={<div className='px-6 pt-4'>
            <p>{t('messages.enterOTP')}</p>
            <p className='text-sm font-normal'>{t('messages.otpMail')}</p>
          </div>} isOpen={!!verification.email} onClose={() => {
      setOtpError('');
      setVerification({
        ...verification,
        email: ''
      });
    }} actionText={t('app.verifyOTP')} loading={loading} data-sentry-element="Modal" data-sentry-source-file="page.tsx">
        <div className='mt-2 px-6'>
          <InputOtp length={6} onValueChange={e => {
          setOtpError('');
          setVerification({
            ...verification,
            otp: e
          });
        }} errorMessage={otpError} isInvalid={!!otpError} data-sentry-element="InputOtp" data-sentry-source-file="page.tsx" />
          {/* <Input
            placeholder='Enter OTP'
            onChange={(e) =>
              setVerification({
                ...verification,
                otp: e.target.value,
              })
            }
            errorMessage={otpError}
            isInvalid={!!otpError}
           /> */}
        </div>
      </Modal>
    </div>;
};
export default SignInPage;